/**
 * BuyerDropshipEnrollmentPanel · Feb 2026 · Iter 207
 * ─────────────────────────────────────────────────────────
 * Buyer-side toggle to enable/pause dropship participation per seller.
 * Toggling OFF hides the buyer from the seller's Dropship sidebar
 * without breaking the underlying agreement (audit trail preserved).
 * Toggling ON re-lists the buyer immediately (matches Faire pattern).
 */
import { useEffect, useState } from "react";
import api from "../../lib/api";
import { toast } from "sonner";
import { Switch } from "../ui/switch";
import { Loader2, Shield, Info } from "lucide-react";

export default function BuyerDropshipEnrollmentPanel() {
  const [prefs, setPrefs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);

  const load = () => {
    setLoading(true);
    api.get("/dropship/buyer-preferences")
      .then(r => setPrefs(r.data || []))
      .catch(() => toast.error("Could not load enrollment preferences"))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const toggle = async (sellerId, enabled) => {
    setBusyId(sellerId);
    try {
      await api.patch(`/dropship/buyer-preferences/${sellerId}`, { enabled });
      toast.success(enabled ? "Dropship enabled with this seller" : "Dropship paused — seller sidebar hidden");
      // Optimistic update
      setPrefs(prev => prev.map(p => p.seller_id === sellerId ? { ...p, enabled } : p));
    } catch { toast.error("Could not update preference"); }
    finally { setBusyId(null); }
  };

  return (
    <div className="rounded-2xl border border-border bg-white p-5" data-testid="buyer-enrollment-panel">
      <div className="flex items-start justify-between gap-4 mb-3 flex-wrap">
        <div>
          <div className="text-[13px] font-semibold flex items-center gap-2">
            <Shield className="w-3.5 h-3.5 text-foreground/50"/>
            Dropship participation
          </div>
          <div className="text-[11.5px] text-foreground/55 mt-0.5 max-w-2xl">
            Toggle a supplier OFF to remove yourself from their Dropship workspace without cancelling the agreement.
            Turn back ON any time — nothing archived.
          </div>
        </div>
      </div>
      {loading ? (
        <div className="py-6 grid place-items-center text-foreground/40">
          <Loader2 className="w-4 h-4 animate-spin"/>
        </div>
      ) : prefs.length === 0 ? (
        <div className="py-8 text-center text-[12px] text-foreground/50 flex flex-col items-center gap-1.5">
          <Info className="w-4 h-4 text-foreground/40"/>
          No signed dropship agreements yet. Once you sign one with a supplier, they appear here.
        </div>
      ) : (
        <div className="space-y-1.5">
          {prefs.map(p => (
            <div key={p.seller_id}
              data-testid={`buyer-enrollment-${p.seller_id}`}
              className="flex items-center gap-3 rounded-lg border border-border bg-secondary/20 px-3 py-2"
            >
              <div className={`w-1.5 h-1.5 rounded-full shrink-0 ${p.enabled ? "bg-emerald-500" : "bg-foreground/30"}`}/>
              <div className="min-w-0 flex-1">
                <div className="text-[12.5px] font-medium truncate">{p.seller_name}</div>
                <div className="text-[10.5px] mono uppercase tracking-[0.14em] text-foreground/45">
                  {p.enabled ? "Active · seller sees your orders" : "Paused · hidden from seller"}
                </div>
              </div>
              <Switch
                checked={p.enabled}
                onCheckedChange={(v) => toggle(p.seller_id, v)}
                disabled={busyId === p.seller_id}
                data-testid={`buyer-enrollment-toggle-${p.seller_id}`}
              />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
